import React, { useState, useRef, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  FaUpload,
  FaCode,
  FaSpinner,
  FaCheckCircle,
  FaTrash,
  FaExternalLinkAlt,
  FaSearch,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import useAxiosInstance from "../../hooks/useAxios";

// ─── Small helpers ────────────────────────────────────────────────────────────

const Badge = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
    {label}
    <button type="button" onClick={onRemove} className="hover:text-blue-900">
      <FaTimes className="text-[10px]" />
    </button>
  </span>
);

// ─── Organisation picker ──────────────────────────────────────────────────────

const OrgPicker = ({ selected, onChange }) => {
  const axiosInstance = useAxiosInstance();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const fetchOrgs = useCallback(
    async (q = "") => {
      setLoading(true);
      try {
        const res = await axiosInstance.get("/admin/organisations/", {
          params: { query: q, pgsz: 50 },
        });
        const list = Array.isArray(res.data)
          ? res.data
          : res.data?.organisations || res.data?.results || [];
        setOrgs(list);
      } catch {
        toast.error("Failed to load organisations");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const debouncedFetch = useDebouncedCallback(fetchOrgs, 400);

  useEffect(() => {
    if (open) fetchOrgs(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (org) => {
    const next = { ...selected };
    if (next[org.org_id]) delete next[org.org_id];
    else next[org.org_id] = org;
    onChange(next);
  };

  const selectedList = Object.values(selected);

  return (
    <div className="relative" ref={panelRef}>
      {/* Trigger */}
      <div
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm border border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 focus-within:ring-2 focus-within:ring-blue-400"
      >
        <div className="flex flex-wrap gap-1 flex-1 min-h-[22px]">
          {selectedList.length === 0 ? (
            <span className="text-gray-400">Select organisations…</span>
          ) : (
            selectedList.map((o) => (
              <Badge
                key={o.org_id}
                label={o.name}
                onRemove={(e) => {
                  e.stopPropagation();
                  toggle(o);
                }}
              />
            ))
          )}
        </div>
        <span className="ml-2 text-gray-400 shrink-0">
          {open ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </div>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
            <FaSearch className="text-gray-400 shrink-0" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                debouncedFetch(e.target.value);
              }}
              placeholder="Search organisations…"
              className="flex-1 text-sm outline-none"
            />
            {loading && (
              <FaSpinner className="text-gray-400 animate-spin shrink-0" />
            )}
          </div>
          <div className="overflow-y-auto max-h-56">
            {orgs.length === 0 && !loading ? (
              <p className="px-4 py-3 text-sm text-gray-400">No results.</p>
            ) : (
              orgs.map((org) => {
                const checked = !!selected[org.org_id];
                return (
                  <label
                    key={org.org_id}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(org)}
                      className="accent-blue-600"
                    />
                    <span className="text-sm text-gray-700 truncate">
                      {org.name}
                    </span>
                    {org.account_type?.name && (
                      <span className="ml-auto text-xs text-gray-400 shrink-0">
                        {org.account_type.name}
                      </span>
                    )}
                  </label>
                );
              })
            )}
          </div>
          {selectedList.length > 0 && (
            <div className="px-3 py-2 text-xs text-right text-gray-400 border-t border-gray-100">
              {selectedList.length} selected
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── User picker ──────────────────────────────────────────────────────────────

const UserPicker = ({ selected, onChange }) => {
  const axiosInstance = useAxiosInstance();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const panelRef = useRef(null);
  const PAGE_SIZE = 20;

  const fetchUsers = useCallback(
    async (q = "", pg = 1, replace = true) => {
      setLoading(true);
      try {
        const res = await axiosInstance.get("/admin/get-org-users/", {
          params: {
            query: q,
            page: pg,
            pgsz: PAGE_SIZE,
          },
        });
        const list = Array.isArray(res.data)
          ? res.data
          : res.data?.users || res.data?.results || [];
        const totalUsers =
          res.data?.total_users ?? res.data?.count ?? list.length;
        setUsers((prev) => (replace ? list : [...prev, ...list]));
        setHasMore(pg * PAGE_SIZE < totalUsers);
        setPage(pg);
      } catch {
        toast.error("Failed to load users");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const debouncedFetch = useDebouncedCallback(
    (q) => fetchUsers(q, 1, true),
    400,
  );

  useEffect(() => {
    if (open) fetchUsers(query, 1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (user) => {
    const next = { ...selected };
    const uid = user.id || user.user_id;
    if (next[uid]) delete next[uid];
    else next[uid] = user;
    onChange(next);
  };

  const selectedList = Object.values(selected);

  const displayName = (u) =>
    [u.first_name, u.last_name].filter(Boolean).join(" ") ||
    u.username ||
    u.email ||
    `User #${u.id || u.user_id}`;

  return (
    <div className="relative" ref={panelRef}>
      {/* Trigger */}
      <div
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm border border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 focus-within:ring-2 focus-within:ring-blue-400"
      >
        <div className="flex flex-wrap gap-1 flex-1 min-h-[22px]">
          {selectedList.length === 0 ? (
            <span className="text-gray-400">Select users…</span>
          ) : (
            selectedList.map((u) => {
              const uid = u.id || u.user_id;
              return (
                <Badge
                  key={uid}
                  label={displayName(u)}
                  onRemove={(e) => {
                    e.stopPropagation();
                    toggle(u);
                  }}
                />
              );
            })
          )}
        </div>
        <span className="ml-2 text-gray-400 shrink-0">
          {open ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </div>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
            <FaSearch className="text-gray-400 shrink-0" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                debouncedFetch(e.target.value);
              }}
              placeholder="Search users…"
              className="flex-1 text-sm outline-none"
            />
            {loading && (
              <FaSpinner className="text-gray-400 animate-spin shrink-0" />
            )}
          </div>
          <div className="overflow-y-auto max-h-56">
            {users.length === 0 && !loading ? (
              <p className="px-4 py-3 text-sm text-gray-400">No results.</p>
            ) : (
              <>
                {users.map((user) => {
                  const uid = user.id || user.user_id;
                  const checked = !!selected[uid];
                  return (
                    <label
                      key={uid}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(user)}
                        className="accent-blue-600"
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-gray-700 truncate">
                          {displayName(user)}
                        </span>
                        {user.org_name && (
                          <span className="block text-xs text-gray-400 truncate">
                            {user.org_name}
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
                {hasMore && (
                  <button
                    type="button"
                    onClick={() => fetchUsers(query, page + 1, false)}
                    disabled={loading}
                    className="w-full py-2 text-xs text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                  >
                    {loading ? "Loading…" : "Load more"}
                  </button>
                )}
              </>
            )}
          </div>
          {selectedList.length > 0 && (
            <div className="px-3 py-2 text-xs text-right text-gray-400 border-t border-gray-100">
              {selectedList.length} selected
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const AddEnterpriseWokeloTool = () => {
  const { toolName: paramToolName } = useParams();
  const axiosInstance = useAxiosInstance();

  const [toolName, setToolName] = useState(paramToolName || "");
  const [htmlFile, setHtmlFile] = useState(null);
  const [htmlFileName, setHtmlFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Audience selection — keyed by id so dedup is free
  const [selectedOrgs, setSelectedOrgs] = useState({});
  const [selectedUsers, setSelectedUsers] = useState({});

  const [tools, setTools] = useState([]);
  const [loadingTools, setLoadingTools] = useState(false);
  const [deleteTool, setDeleteTool] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTools = async () => {
    setLoadingTools(true);
    try {
      const res = await axiosInstance.get("/wkl/wkl_html_tools/");
      setTools(Array.isArray(res.data) ? res.data : res.data?.results || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load tools");
    } finally {
      setLoadingTools(false);
    }
  };

  useEffect(() => {
    fetchTools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async () => {
    if (!deleteTool) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/wkl/wkl_html_tools/${deleteTool.id}/`);
      toast.success("Tool deleted successfully");
      setTools((prev) => prev.filter((t) => t.id !== deleteTool.id));
      setDeleteTool(null);
      fetchTools();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete tool");
    } finally {
      setDeleting(false);
    }
  };

  const routeSlug = toolName.trim() || "<route-name>";
  const previewUrl = `app.wokelo.ai/insights/${routeSlug}`;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith(".html")) {
      toast.error("Only .html files are accepted");
      e.target.value = "";
      return;
    }
    setHtmlFile(file);
    setHtmlFileName(file.name);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!toolName.trim()) {
      toast.error("Route name is required");
      return;
    }
    if (!htmlFile) {
      toast.error("HTML file is required");
      return;
    }

    const orgIds = Object.keys(selectedOrgs).map(Number);
    const userIds = Object.keys(selectedUsers).map(Number);
    if (orgIds.length === 0 && userIds.length === 0) {
      toast.error(
        "Select at least one organisation or user to show this tool to",
      );
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("files", htmlFile);
      formData.append("file_name", htmlFile.name);
      formData.append("type", "file");

      const uploadRes = await axiosInstance.post(
        "/assets/upload_files_for_email_sender/",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const htmlFilePath = uploadRes.data.path;
      setUploading(false);

      setSubmitting(true);
      await axiosInstance.post("/wkl/wkl_html_tools/", {
        name: toolName.trim(),
        html_file_path: htmlFilePath,
        route: toolName.trim(),
        orgs: orgIds,
        users: userIds,
        meta: {},
      });

      toast.success("Enterprise tool added successfully");
      setToolName("");
      setHtmlFile(null);
      setHtmlFileName("");
      setSelectedOrgs({});
      setSelectedUsers({});
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchTools();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to add tool");
    } finally {
      setUploading(false);
      setSubmitting(false);
    }
  };

  const isBusy = uploading || submitting;

  const buttonLabel = uploading
    ? "Uploading file…"
    : submitting
      ? "Registering tool…"
      : "Add Enterprise Tool";

  return (
    <div className="max-w-4xl p-5">
      <h1 className="mb-1 text-2xl font-semibold">Add New Enterprise Tool</h1>
      <p className="mb-6 text-sm text-gray-500">
        Deploy an HTML tool to the Wokelo enterprise app and control which
        organisations and users can see it.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Route name */}
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Route Name
          </label>
          <input
            type="text"
            value={toolName}
            onChange={(e) =>
              setToolName(e.target.value.replace(/\s+/g, "-").toLowerCase())
            }
            placeholder="my-enterprise-tool"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <div className="flex items-center gap-2 px-3 py-2 mt-2 border border-gray-200 rounded-lg bg-gray-50">
            <FaCode className="text-gray-400 shrink-0" />
            <span className="font-mono text-sm text-gray-600 break-all">
              {previewUrl}
            </span>
          </div>
        </div>

        {/* HTML file */}
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            HTML File
          </label>
          <div
            onClick={() => !isBusy && fileInputRef.current?.click()}
            className={`w-full border-2 border-dashed rounded-lg px-4 py-6 flex flex-col items-center gap-2 transition-colors ${
              isBusy
                ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                : "border-gray-300 cursor-pointer hover:border-blue-400 hover:bg-blue-50"
            }`}
          >
            {htmlFileName ? (
              <>
                <FaCheckCircle className="text-xl text-green-500" />
                <span className="text-sm font-medium text-green-600">
                  {htmlFileName}
                </span>
                {!isBusy && (
                  <span className="text-xs text-gray-400">
                    Click to change file
                  </span>
                )}
              </>
            ) : (
              <>
                <FaUpload className="text-xl text-gray-400" />
                <span className="text-sm text-gray-500">
                  Click to select an <strong>.html</strong> file
                </span>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".html"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {/* Audience — orgs */}
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Organisations{" "}
            <span className="font-normal text-gray-400">
              (optional if users are selected)
            </span>
          </label>
          <OrgPicker selected={selectedOrgs} onChange={setSelectedOrgs} />
        </div>

        {/* Audience — users */}
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Users{" "}
            <span className="font-normal text-gray-400">
              (optional if organisations are selected)
            </span>
          </label>
          <UserPicker selected={selectedUsers} onChange={setSelectedUsers} />
        </div>

        <button
          type="submit"
          disabled={isBusy}
          className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isBusy && <FaSpinner className="animate-spin" />}
          {buttonLabel}
        </button>
      </form>

      {/* Tools list */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">All Enterprise Tools</h2>
          {loadingTools && <FaSpinner className="text-gray-400 animate-spin" />}
        </div>

        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 bg-gray-50">
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Route</th>
                <th className="px-4 py-2 font-medium">Orgs</th>
                <th className="px-4 py-2 font-medium">Users</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tools.length === 0 && !loadingTools ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-6 text-center text-gray-400"
                  >
                    No enterprise tools yet.
                  </td>
                </tr>
              ) : (
                tools.map((tool) => (
                  <tr key={tool.id} className="border-t border-gray-100">
                    <td className="px-4 py-2 font-medium text-gray-800">
                      {tool.name}
                    </td>
                    <td className="px-4 py-2">
                      <a
                        href={`https://app.wokelo.ai/insights/${tool.route}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 font-mono text-gray-600 hover:text-blue-600"
                      >
                        {tool.route}
                        <FaExternalLinkAlt className="text-[10px]" />
                      </a>
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {Array.isArray(tool.orgs)
                        ? tool.orgs.length > 0
                          ? tool.orgs.length === 1
                            ? tool.orgs[0]?.name || "1 org"
                            : `${tool.orgs.length} orgs`
                          : "—"
                        : tool.org_count != null
                          ? tool.org_count || "—"
                          : "—"}
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {Array.isArray(tool.users)
                        ? tool.users.length > 0
                          ? tool.users.length === 1
                            ? tool.users[0]?.username || "1 user"
                            : `${tool.users.length} users`
                          : "—"
                        : tool.user_count != null
                          ? tool.user_count || "—"
                          : "—"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => setDeleteTool(tool)}
                        className="p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-red-500"
                        title="Delete tool"
                      >
                        <FaTrash className="text-sm" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirmation */}
      {!!deleteTool && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="w-full max-w-sm p-6 bg-white rounded-lg shadow-xl">
            <h2 className="mb-2 text-lg font-semibold">Delete tool?</h2>
            <p className="mb-4 text-sm text-gray-500">
              This will permanently delete{" "}
              <span className="font-medium text-gray-700">
                {deleteTool.name}
              </span>{" "}
              — this action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTool(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {deleting && <FaSpinner className="animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddEnterpriseWokeloTool;
